import React from 'react';
import {
  ActionIcon, FontWeight, HStack, ICON_EYE_OUTLINE, Stack, Text, UserPreview,
  SimpleTable,
} from '@doveit/bricks';
import { SearchCriteria } from '../../../providers/api/dtos';

import DateViewer from '../../../components/date-viewer/DateViewer';
import { NO_VALUE_SYMBOL } from '../../../property/utils';
import { goToDetailPage } from '../../../utils/navigate/utils';
import { ReferenceType } from '../../../domain/types';
import { setVerticalEllipsis } from '../../../utils/styles/verticalEllipsis.style';
import { getGeoCriterionLabel } from '../../utils/getGeoCriterionLabel';
import { residentialPropertyTypeLabels } from '../../../labels';
import ViewContactPopoverPreview from '../../../contact/containers/view-contact-popover-preview/ViewContactPopoverPreview';
import { MatchingSearchCriteriaContact } from '../../types';
import ViewSearchCriteriaPopoverPreview from '../../container/view-search-criteria-popover-preview/ViewSearchCriteriaPopoverPreview';
import SearchCriteriaAvatar from '../search-criteria-avatar/SearchCriteriaAvatar';

export interface MatchingSearchCriteriaPreviewTableRowProps {
  searchCriteria: SearchCriteria,
  contact: MatchingSearchCriteriaContact,
}

const MatchingSearchCriteriaPreviewTableRow: React.FC<MatchingSearchCriteriaPreviewTableRowProps> = ({
  searchCriteria,
  contact,
}) => {
  const propertyTypes = React.useMemo(() => searchCriteria?.propertyTypes
    .map((propertyType) => residentialPropertyTypeLabels[propertyType])
    .join(', '), [searchCriteria?.propertyTypes]);

  const goToSearchCriteriaPage = React.useCallback(() => {
    goToDetailPage(searchCriteria.id!, ReferenceType.SEARCH_CRITERIA);
  }, [searchCriteria.id]);

  return (
    <SimpleTable.Row
      data-testid={`search-criteria-preview-table-row-${searchCriteria.id}`}
      aria-label="Informazioni ricerca"
    >
      <SimpleTable.Cell aria-label="Informazioni contatto">
        <ViewContactPopoverPreview
          contact={contact}
          trigger={(
            <UserPreview
              name={contact.name || NO_VALUE_SYMBOL}
              size="S"
            >
              {contact.phoneNumber || NO_VALUE_SYMBOL}
            </UserPreview>
          )}
        />
      </SimpleTable.Cell>
      <SimpleTable.Cell aria-label="Data di creazione">
        <Stack gap={50}>
          <Text.Mini transform="uppercase">
            Aggiunta
          </Text.Mini>
          <Text.Caption>
            <DateViewer
              stringDate={searchCriteria.createdAt!}
              checkIfToday
            />
          </Text.Caption>
        </Stack>
      </SimpleTable.Cell>
      <SimpleTable.Cell aria-label="Tipologia e geo" width="100%">
        <ViewSearchCriteriaPopoverPreview
          searchCriteria={searchCriteria}
          trigger={(
            <HStack gap={100} wrap="nowrap">
              <SearchCriteriaAvatar size="S" />
              <Stack gap={0}>
                <Text.BodySmall css={setVerticalEllipsis(1)}>
                  {propertyTypes}
                </Text.BodySmall>
                <Text.BodySmall
                  fontWeight={FontWeight.MEDIUM}
                  css={setVerticalEllipsis(1)}
                >
                  {searchCriteria.geo.map(getGeoCriterionLabel).join(', ')}
                </Text.BodySmall>
              </Stack>
            </HStack>
          )}
        />
      </SimpleTable.Cell>
      <SimpleTable.Cell aria-label="Azioni" align="right">
        <ActionIcon
          aria-label="Visualizza ricerca"
          label="Visualizza"
          size="S"
          icon={{ path: ICON_EYE_OUTLINE }}
          onClick={goToSearchCriteriaPage}
        />
      </SimpleTable.Cell>
    </SimpleTable.Row>
  );
};

export default MatchingSearchCriteriaPreviewTableRow;
